import React from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const fmtTime = (ms) => {
    if (!ms) return "--";
    const d = new Date(ms);
    return d.toLocaleTimeString("en-US", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
};
const short = (s) => (s ? String(s).slice(0, 8) : "--");

const SideBadge = ({ side }) => {
    const isBuy = side === "BUY";
    return (
        <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.12em]"
            style={{
                background: isBuy ? "rgba(43,227,139,0.10)" : "rgba(255,92,108,0.10)",
                color: isBuy ? "#2BE38B" : "#FF5C6C",
                border: `1px solid ${isBuy ? "rgba(43,227,139,0.25)" : "rgba(255,92,108,0.25)"}`,
            }}
        >
            {isBuy ? <ArrowUp size={10} /> : <ArrowDown size={10} />} {side}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const map = {
        OPEN: { color: "#3DA7FF", bg: "rgba(61,167,255,0.10)", border: "rgba(61,167,255,0.25)" },
        PENDING: { color: "#A7B4C6", bg: "rgba(167,180,198,0.10)", border: "rgba(167,180,198,0.25)" },
        PARTIALLY_FILLED: { color: "#F0B94A", bg: "rgba(240,185,74,0.10)", border: "rgba(240,185,74,0.30)" },
        FILLED: { color: "#2BE38B", bg: "rgba(43,227,139,0.10)", border: "rgba(43,227,139,0.25)" },
        CANCELED: { color: "#6F7D92", bg: "rgba(167,180,198,0.06)", border: "rgba(167,180,198,0.15)" },
        REJECTED: { color: "#FF5C6C", bg: "rgba(255,92,108,0.10)", border: "rgba(255,92,108,0.25)" },
    };
    const v = map[status] || map.PENDING;
    const label = status === "PARTIALLY_FILLED" ? "PARTIAL" : status;
    return (
        <span
            className="inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.12em] font-mono"
            style={{ background: v.bg, color: v.color, border: `1px solid ${v.border}` }}
        >
            {label}
        </span>
    );
};

export const OpenOrdersTable = ({ orders = [], onGridClick }) => (
    <div className="panel" data-testid="open-orders-panel">
        <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">Open Orders</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6F7D92]">{orders.length}</div>
        </div>
        <ScrollArea className="h-[280px]">
            {/* Horizontal scroll for narrow (mobile) viewports — without this the
                rightmost columns were simply cut off. */}
            <div className="overflow-x-auto">
            <table className="w-full min-w-[440px] text-xs" data-testid="open-orders-table">
                <thead className="sticky top-0" style={{ background: "#0F1620" }}>
                    <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                        <th className="px-3 py-2">Side</th>
                        <th className="px-3 py-2">Grid</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-[#3A4759]">
                                No open orders.
                            </td>
                        </tr>
                    )}
                    {orders.map((o) => (
                        <tr
                            key={o.client_order_id || `${o.grid_id}-${o.side}-${o.price}`}
                            className="border-t border-[#13202D] hover:bg-[#121C28]"
                            data-testid="open-orders-row"
                        >
                            <td className="px-3 py-2"><SideBadge side={o.side} /></td>
                            <td className="px-3 py-2">
                                <button
                                    onClick={() => onGridClick && onGridClick(o.grid_id)}
                                    className="btn-anim font-mono text-[#2EE6D6] hover:text-[#43F0E0] underline-offset-2 hover:underline"
                                    data-testid={`open-orders-grid-${o.grid_id}`}
                                >
                                    {short(o.grid_id)}
                                </button>
                            </td>
                            <td className="px-3 py-2 text-right font-mono">${Number(o.price).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">
                                {o.qty}
                                {Number(o.cumulative_filled_qty || 0) > 0 && (
                                    <div
                                        className="text-[10px] text-[#F0B94A]"
                                        data-testid={`open-orders-fill-${o.grid_id}`}
                                    >
                                        {Number(o.cumulative_filled_qty).toFixed(6)} filled
                                        {o.fill_pct != null && <> · {Number(o.fill_pct).toFixed(1)}%</>}
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </ScrollArea>
    </div>
);

export const TradeHistoryTable = ({ trades = [], onGridClick }) => (
    <div className="panel" data-testid="trade-history-panel">
        <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">Trade History</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6F7D92]">{trades.length}</div>
        </div>
        <ScrollArea className="h-[280px]">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-xs" data-testid="trade-history-table">
                <thead className="sticky top-0" style={{ background: "#0F1620" }}>
                    <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                        <th className="px-3 py-2">Filled</th>
                        <th className="px-3 py-2">Side</th>
                        <th className="px-3 py-2">Grid</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Realized PnL</th>
                    </tr>
                </thead>
                <tbody>
                    {trades.length === 0 && (
                        <tr>
                            <td colSpan={6} className="px-3 py-6 text-center text-[#3A4759]">
                                No trades yet.
                            </td>
                        </tr>
                    )}
                    {trades.map((t, i) => {
                        const isSell = t.side === "SELL";
                        const pnl = Number(t.realized_pnl || 0);
                        return (
                            <tr
                                key={`${t.grid_id}-${t.side}-${t.filled_at_ms || i}`}
                                className="border-t border-[#13202D] hover:bg-[#121C28]"
                                data-testid="trade-history-row"
                            >
                                <td className="px-3 py-2 font-mono text-[#A7B4C6]">{fmtTime(t.filled_at_ms)}</td>
                                <td className="px-3 py-2"><SideBadge side={t.side} /></td>
                                <td className="px-3 py-2">
                                    <button
                                        onClick={() => onGridClick && onGridClick(t.grid_id)}
                                        className="btn-anim font-mono text-[#2EE6D6] hover:text-[#43F0E0] underline-offset-2 hover:underline"
                                        data-testid={`trade-history-grid-${t.grid_id}`}
                                    >
                                        {short(t.grid_id)}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right font-mono">${Number(t.price).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{t.qty}</td>
                                <td
                                    className="px-3 py-2 text-right font-mono"
                                    style={{ color: isSell ? (pnl >= 0 ? "#2BE38B" : "#FF5C6C") : "#6F7D92" }}
                                >
                                    {isSell ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(4)}` : "—"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
        </ScrollArea>
    </div>
);
